import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import {
  useOrderLifecycle,
  useOrderSettings,
  useUpdateOrderLifecycle,
  useUpdateOrderSettings,
} from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { FeedbackSeverity } from '../../lib';
import type { OmsMode, OrderStatusTransition } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import { OMS_MODE_OPTIONS } from '../options';

function TransitionsEditor({
  value,
  onChange,
  disabled,
}: {
  value: OrderStatusTransition[];
  onChange: (value: OrderStatusTransition[]) => void;
  disabled: boolean;
}) {
  const update = (index: number, patch: Partial<OrderStatusTransition>) =>
    onChange(
      value.map((transition, i) => (i === index ? { ...transition, ...patch } : transition)),
    );
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const add = () => onChange([...value, { from: '', to: [] }]);

  return (
    <Stack spacing={1.5}>
      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No transitions defined.
        </Typography>
      ) : null}
      {value.map((transition, index) => (
        <Stack
          key={index}
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            size="small"
            label="From"
            value={transition.from}
            disabled={disabled}
            onChange={(event) => update(index, { from: event.target.value })}
            sx={{ minWidth: 170 }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              disabled={disabled}
              options={[]}
              value={transition.to}
              onChange={(_event, next) => update(index, { to: next as string[] })}
              renderTags={(tags, getTagProps) =>
                tags.map((tag, i) => {
                  const { key, ...tagProps } = getTagProps({ index: i });
                  return <Chip size="small" label={tag} key={key} {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="To" placeholder="Add status" />
              )}
            />
          </Box>
          <Box>
            <Button
              color="error"
              size="small"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => remove(index)}
              disabled={disabled}
            >
              Remove
            </Button>
          </Box>
        </Stack>
      ))}
      <Box>
        <Button startIcon={<AddRoundedIcon />} onClick={add} disabled={disabled} size="small">
          Add transition
        </Button>
      </Box>
    </Stack>
  );
}

function OrderLifecycleCard({
  canManage,
  onFeedback,
}: {
  canManage: boolean;
  onFeedback: (severity: FeedbackSeverity, message: string) => void;
}) {
  const query = useOrderLifecycle();
  const update = useUpdateOrderLifecycle();
  const form = useSettingsForm(query.data);

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => onFeedback('success', 'Order lifecycle saved.'),
      onError: (error) => {
        onFeedback('error', error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  const action = canManage ? (
    <Stack direction="row" spacing={1}>
      <Button size="small" onClick={form.reset} disabled={!form.dirty || update.isPending}>
        Reset
      </Button>
      <Button
        size="small"
        variant="contained"
        onClick={handleSave}
        disabled={!form.dirty || update.isPending}
        startIcon={
          update.isPending ? <CircularProgress size={14} color="inherit" /> : <SaveRoundedIcon />
        }
      >
        Save lifecycle
      </Button>
    </Stack>
  ) : undefined;

  const draft = form.draft;

  return (
    <SettingsCard
      title="Order lifecycle"
      description="Status set and the allowed transitions between them."
      icon={<AltRouteRoundedIcon />}
      action={action}
      divided={false}
    >
      <Box sx={{ pt: 1 }}>
        {query.isLoading ? (
          <LoadingState label="Loading lifecycle…" fullHeight={false} />
        ) : query.isError ? (
          <ErrorState
            title="Couldn't load lifecycle"
            message={query.error?.message ?? 'An unexpected error occurred.'}
            onRetry={() => void query.refetch()}
          />
        ) : draft ? (
          <Stack spacing={2}>
            <TextInputField
              label="Lifecycle profile"
              value={draft.lifecycleProfile}
              onChange={(value) => form.setField('lifecycleProfile', value)}
              disabled={!canManage}
            />
            <ChipListField
              label="Statuses"
              description="All order statuses in this profile."
              value={draft.statuses}
              onChange={(value) => form.setField('statuses', value)}
              disabled={!canManage}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Allowed transitions
              </Typography>
              <TransitionsEditor
                value={draft.transitions}
                onChange={(value) => form.setField('transitions', value)}
                disabled={!canManage}
              />
            </Box>
          </Stack>
        ) : null}
      </Box>
    </SettingsCard>
  );
}

/** Order behaviour (OMS mode, auto-cancel) and the order lifecycle. */
export function OrderSettingsPage() {
  const { canView, canManage, canManageLifecycle } = useDomainPermissions('orders');
  const query = useOrderSettings();
  const update = useUpdateOrderSettings();
  const form = useSettingsForm(query.data);
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Order settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Order settings"
      subtitle="OMS mode, auto-cancel, confirmation and lifecycle transitions."
      icon={<ReceiptLongRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Order behaviour" icon={<TuneRoundedIcon />}>
              <SelectField
                label="OMS mode"
                description="Where orders are managed."
                value={draft.omsMode}
                options={OMS_MODE_OPTIONS}
                onChange={(value) => form.setField('omsMode', value as OmsMode)}
                disabled={disabled}
              />
              <SwitchField
                label="Confirmation required"
                description="Require manual confirmation before fulfilment."
                checked={draft.confirmationRequired}
                onChange={(value) => form.setField('confirmationRequired', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Customer-visible status history"
                checked={draft.customerVisibleStatusHistory}
                onChange={(value) => form.setField('customerVisibleStatusHistory', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Auto-cancel" icon={<ReceiptLongRoundedIcon />}>
              <SwitchField
                label="Auto-cancel unpaid orders"
                checked={draft.autoCancelEnabled}
                onChange={(value) => form.setField('autoCancelEnabled', value)}
                disabled={disabled}
              />
              <NumberField
                label="Auto-cancel after"
                description="Cancel unpaid orders after this delay."
                value={draft.autoCancelAfterMinutes}
                onChange={(value) => form.setField('autoCancelAfterMinutes', value)}
                disabled={disabled || !draft.autoCancelEnabled}
                min={0}
                suffix="min"
              />
            </SettingsCard>

            <OrderLifecycleCard canManage={canManageLifecycle} onFeedback={notify} />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
